import React, { useState, useEffect } from "react";
import axios from "axios";
import "./OwnerPortal.css";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";

export default function OwnerPortal() {
  const [view, setView] = useState("products");
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showOrderPopup, setShowOrderPopup] = useState(false);
  const [latestOrder, setLatestOrder] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  // ✅ INITIAL LOAD + POLLING
  useEffect(() => {
    const refreshData = () => {
      fetchProducts();
      fetchAgents();
      fetchOrders();
    };
    refreshData();
    const interval = setInterval(refreshData, 10000);
    return () => clearInterval(interval);
  }, []);

  // ✅ SOCKET LISTENER
  useEffect(() => {
    const socket = io(process.env.REACT_APP_BACKEND_URL);
    socket.on("new_order", (data) => {
      fetchOrders(); // Always refresh the orders list
      if (data.order) {
        setLatestOrder(data);
        setShowOrderPopup(true);
      }
    });
    return () => socket.disconnect();
  }, []);

  // ================= FETCHING =================
  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/delivery/products`);
      setItems(res.data.products || []);
    } catch (e) { console.error(e); }
  };

  const fetchAgents = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/delivery/owner/agents`, { credentials: "include" });
      const data = await res.json();
      setAgents(data.agents || data || []);
    } catch (err) { console.error(err); }
  };

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/delivery/owner/orders`, { credentials: "include" });
      const data = await res.json();
      setOrders(data.orders || data || []);
    } catch (err) { console.error(err); }
  };

  // ================= ACTIONS =================
  const handleSave = async () => {
    if (!selectedItem.name) return alert("Name is required");
    try {
      if (view === "products") {
        if (selectedItem.id) {
          await axios.put(`${process.env.REACT_APP_BACKEND_URL}/delivery/update`, selectedItem);
        } else {
          await axios.post(`${process.env.REACT_APP_BACKEND_URL}/delivery/products`, selectedItem);
        }
        fetchProducts();
      } else if (view === "agents") {
        if (selectedItem.agent_id) {
          await axios.put(`${process.env.REACT_APP_BACKEND_URL}/delivery/owner/agents/${selectedItem.agent_id}`, selectedItem, { withCredentials: true });
        } else {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/delivery/owner/agents`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(selectedItem),
          });
        }
        fetchAgents();
      }
      setSelectedItem(null);
    } catch (err) { console.error(err); }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setSelectedItem({ ...selectedItem, image_url: reader.result });
    reader.readAsDataURL(file);
  };

  const handleAcceptOrder = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/delivery/owner/orders/${id}/accept`, { method: "POST", credentials: "include" });
      fetchOrders();
      setShowOrderPopup(false);
    } catch (err) { console.error(err); }
  };

  const handleDeclineOrder = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/delivery/owner/orders/${id}/decline`, { method: "POST", credentials: "include" });
      fetchOrders();
      setShowOrderPopup(false);
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this item?")) return;
    try {
      if (view === "products") {
        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/delivery/delete/${id}`);
        fetchProducts();
      } else if (view === "agents") {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/delivery/owner/agents/${id}`, { method: "DELETE", credentials: "include" });
        fetchAgents();
      }
    } catch (err) { console.error(err); }
    setSelectedItem(null);
  };

  const listData = view === "products" 
    ? items.filter(i => (i.name || "").toLowerCase().includes(searchTerm.toLowerCase()))
    : view === "agents"
    ? agents.filter(a => (a.name || "").toLowerCase().includes(searchTerm.toLowerCase()))
    : (Array.isArray(orders) ? orders : []).filter(o => JSON.stringify(o).toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="owner-portal-wrapper">
      <aside className="owner-portal-sidebar">
        <div className="owner-portal-brand">OWNER PORTAL</div>
        <nav>
          <button onClick={() => { setView("products"); setSelectedItem(null); }} className={`owner-portal-nav-item ${view === "products" ? "active" : ""}`}>Products</button>
          <button onClick={() => { setView("agents"); setSelectedItem(null); }} className={`owner-portal-nav-item ${view === "agents" ? "active" : ""}`}>Agents</button>
          <button onClick={() => { setView("orders"); setSelectedItem(null); }} className={`owner-portal-nav-item ${view === "orders" ? "active" : ""}`}>Orders</button>
        </nav>
      </aside>

      <div className="owner-portal-main">
        <header className="owner-portal-header">
          <button className="owner-portal-back-btn" onClick={() => navigate("/mars-mart/home")}>← Back</button>
          <input type="text" placeholder="Search..." className="owner-portal-search" onChange={(e) => setSearchTerm(e.target.value)} />
        </header>

        <div className="owner-portal-content-area">
          <section className={`owner-portal-list ${view === "orders" ? "full-width" : ""}`}>
            <h4 className="owner-portal-section-title">Manage {view}</h4>
            {listData.map((item) => {
              const isActive = (view === "products" && selectedItem?.id === item.id) || 
                               (view === "agents" && selectedItem?.agent_id === item.agent_id);

              return (
                <div 
                  key={item.id || item.order_id || item.agent_id} 
                  className={`owner-portal-card ${view === "orders" ? "order-style" : ""} ${isActive ? "active" : ""}`}
                  onClick={() => view !== "orders" && setSelectedItem(item)}
                >
                  <div className="owner-card-info-wrapper">
                    {view === "orders" ? (
                      <div className="order-items-row-container">
                        <div className="order-header-info">
                           <span className="order-id-tag">Order #{item.order_id}</span>
                           <span
                             className={`order-status-pill ${item.status === "UNASSIGNED" ? "order-status-pill-active" : ""}`}
                             style={
                               item.status === "UNASSIGNED"
                                 ? { background: "#dcfce7", color: "#166534" }
                                 : undefined
                             }
                           >
                             {item.status === "UNASSIGNED"
                               ? "Accepted (Waiting for Agent)"
                               : item.status.replace(/_/g, " ")}
                           </span>
                        </div>
                        <div className="order-items-image-wrap">
                          {item.items?.map((prod, idx) => (
                            <div key={idx} className="order-mini-item">
                               <img src={prod.image_url} alt={prod.name} />
                               <span className="item-qty-badge">{prod.quantity}</span>
                            </div>
                          ))}
                        </div>
                        <div className="owner-portal-card-sub">Total: ₹{item.total_amount}</div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <img src={item.image_url || "https://cdn-icons-png.flaticon.com/512/1238/1238318.png"} alt="item" className="owner-portal-avatar" />
                        <div>
                          <div className="owner-portal-card-title">{item.name}</div>
                          <div className="owner-portal-card-sub">{view === "products" ? `₹${item.price}` : item.phone}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  {view !== "orders" && <button className="owner-portal-delete-icon" onClick={(e) => handleDelete(item.id || item.agent_id, e)}>🗑️</button>}
                  {view === "orders" && item.status === "PENDING" && (
                    <div className="owner-portal-list-actions" onClick={(e) => e.stopPropagation()}>
                      <button className="btn-mini-accept" onClick={() => handleAcceptOrder(item.order_id)}>Accept</button>
                      <button className="btn-mini-decline" onClick={() => handleDeclineOrder(item.order_id)}>Decline</button>
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          {/* FULL EDITOR PANEL RESTORED */}
          {view !== "orders" && (
            <section className="owner-portal-editor">
              {selectedItem ? (
                <div className="owner-portal-animate">
                  <h3 style={{ marginBottom: "24px" }}>{selectedItem.id || selectedItem.agent_id ? "Edit Details" : "Add New"}</h3>
                  <div className="owner-portal-upload-box">
                    {selectedItem.image_url ? <img src={selectedItem.image_url} alt="preview" /> : <span style={{ color: "#999" }}>Click to upload photo</span>}
                    <input type="file" style={{ opacity: 0, position: "absolute", inset: 0, cursor: "pointer" }} onChange={handleImageChange} />
                  </div>
                  <div className="owner-portal-field-group">
                    <label className="owner-portal-label">Name</label>
                    <input className="owner-portal-input" value={selectedItem.name || ""} onChange={(e) => setSelectedItem({ ...selectedItem, name: e.target.value })} />
                  </div>
                  {view === "products" && (
                    <div className="owner-portal-field-group">
                      <label className="owner-portal-label">Price (₹)</label>
                      <input className="owner-portal-input" type="number" value={selectedItem.price || ""} onChange={(e) => setSelectedItem({ ...selectedItem, price: e.target.value })} />
                    </div>
                  )}
                  {view === "agents" && (
                    <div className="owner-portal-field-group">
                      <label className="owner-portal-label">Phone</label>
                      <input className="owner-portal-input" value={selectedItem.phone || ""} onChange={(e) => setSelectedItem({ ...selectedItem, phone: e.target.value })} />
                    </div>
                  )}
                  <button className="owner-portal-submit-btn" onClick={handleSave}>SAVE CHANGES</button>
                  <button className="owner-portal-input" style={{ marginTop: "12px", cursor: "pointer" }} onClick={() => setSelectedItem(null)}>Cancel</button>
                </div>
              ) : (
                <div className="empty-editor-msg">
                   <p>Select an item to edit or</p>
                   <button onClick={() => setSelectedItem({})} className="add-new-link">Add New +</button>
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {showOrderPopup && latestOrder && view !== "orders" && (
        <div className="owner-order-popup">
          <div className="owner-order-popup-card">
            <h3>🚨 New Order</h3>
            <h3>
              {(() => {
                const items = latestOrder?.order?.items || [];
                const parsedItems = typeof items === 'string' ? JSON.parse(items) : items;
                return parsedItems[0]?.name || "New Item";
              })()}
            </h3>
            <p><strong>ID:</strong> {latestOrder.order.order_id}</p>
            <div className="owner-order-actions">
              <button className="accept-btn" onClick={() => handleAcceptOrder(latestOrder.order.order_id)}>Accept</button>
              <button className="decline-btn" onClick={() => handleDeclineOrder(latestOrder.order.order_id)}>Decline</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
